import { Input } from 'src/design-system'
import cn from 'classnames'
import React, { useState } from 'react'
import { AiGenerateButton } from 'components/ai/ai-generate-button'
import { Editor, EditorContext } from 'components/atoms/editor'
import { HtmlContent } from 'components/atoms/editor/html-content'
import { ModifiablePositionAttributes, Position } from 'store/modules/positions'
import { onUseGeneratedPositionDescription } from './on-position-description-use'
import styles from './styles.module.scss'
import { Warning, X } from '@phosphor-icons/react'
import { store } from 'store/index'

const NAME_MAX_LENGTH = 255

type PositionModalHeaderProps = {
  editable?: boolean
  errors: string[]
  formState: Partial<ModifiablePositionAttributes>
  onBlur: (
    field: keyof ModifiablePositionAttributes,
    resetOnFailure?: boolean,
  ) => void
  onChange: (
    field: keyof ModifiablePositionAttributes,
    value: string | null,
  ) => void
  position: Position
}

export const PositionModalHeader = (props: PositionModalHeaderProps) => {
  const { editable, errors, formState, onBlur, onChange, position } = props

  const editor = React.useRef<EditorContext>(null)
  const [positionHealthIssues, setPositionHealthIssues] = useState<string[]>([])

  React.useEffect(() => {
    const loadHealthAudit = async () => {
      try {
        const response = await fetch(
          `/api/v1/health_audits/position_audit?position_id=${position.id}`,
        )
        if (response.ok) {
          const data = await response.json()
          setPositionHealthIssues(data.messages)
        }
      } catch (error) {
        console.error('Position health audit check failed:', error)
      }
    }

    const hasPositionId = position.id
    const isHealthAuditEnabled = store.currentUser?.org?.enableHealthAudit
    const isUserAdmin = store.currentUser?.isAdmin

    if (hasPositionId && isHealthAuditEnabled && isUserAdmin) {
      loadHealthAudit()
    }

    // Add event listener for health audit updates
    const handleHealthAuditUpdate = (
      event: CustomEvent<{ messages: string[] }>,
    ) => {
      setPositionHealthIssues(event.detail.messages)
    }

    document.addEventListener(
      'positionHealthAuditUpdated',
      handleHealthAuditUpdate as EventListener,
    )

    return () => {
      document.removeEventListener(
        'positionHealthAuditUpdated',
        handleHealthAuditUpdate as EventListener,
      )
    }
  }, [position.id])

  return (
    <>
      {positionHealthIssues.length > 0 &&
        store.currentUser?.org?.enableHealthAudit &&
        store.currentUser?.isAdmin && (
          <>
            {positionHealthIssues.map((issue, i) => (
              <div
                key={i}
                className="bg-yellow-100 text-yellow-800 px-5 py-3 rounded-md m-3 flex items-center justify-between gap-2"
              >
                <div className="flex items-center gap-2">
                  <Warning weight="bold" />
                  <span className="text-sm">{issue}</span>
                </div>
                <X
                  className="cursor-pointer text-yellow-800"
                  weight="bold"
                  onClick={() =>
                    setPositionHealthIssues((currentIssues) =>
                      currentIssues.filter((_, index) => index !== i),
                    )
                  }
                />
              </div>
            ))}
          </>
        )}

      {editable && (
        <div className="-mx-2">
          <Input
            className="text-3xl font-bold py-2 pl-2 hover:border-gray-200 focus-within:!border-theme-60 focus-within:!shadow-none"
            isInvalid={errors.includes('name')}
            key={`input-${position.id}`}
            maxLength={NAME_MAX_LENGTH}
            naked
            name="name"
            onBlur={() => onBlur('name', true)}
            onChange={(e) => onChange('name', e.target.value)}
            value={formState.name}
          />
          <div className="relative group">
            <Editor
              className="border-transparent hover:border-gray-200 focus-within:!border-theme-60 focus-within:!shadow-none pr-12 text-gray-600"
              initialContent={position.description || ''}
              invalid={errors.includes('description')}
              key={`editor-${position.id}`}
              name="description"
              onBlur={() => onBlur('description')}
              onChange={(value) => onChange('description', value)}
              placeholder="Add description"
              ref={editor}
              textSize="base"
            />
            {editable && (
              <AiGenerateButton
                className="absolute right-1 bottom-1 opacity-0 group-hover:opacity-100 transition-all ease-in-out duration-200"
                source="inline-position-description"
                inputs={{ positionName: formState.name }}
                contentType="positionDescription"
                onUse={async (output) =>
                  onUseGeneratedPositionDescription(output, position, editor)
                }
                tooltipContent="Generate a description using our AI Assistant"
              />
            )}
          </div>
        </div>
      )}

      {!editable && (
        <>
          <div className="font-bold text-3xl">{position.name}</div>
          {position.description && (
            <HtmlContent className={cn('mt-4', styles.description)}>
              {position.description}
            </HtmlContent>
          )}
        </>
      )}
    </>
  )
}
